import { DealerContextDropdown } from "@components/DealerContextSelector/DealerContextDropdown";
import {
  type DealershipWithGroup,
  useDealerContext,
} from "@hooks/useDealerContext";
import { DEALER_CONTEXT_TYPE, type DealerContext } from "@lyteflo/utils";
import { useScrollableOverlayStore } from "@stores/scrollableOverlayStore";
import { useTranslation } from "react-i18next";

const findCurrentLabel = (
  context: DealerContext,
  dealerGroups: ReturnType<typeof useDealerContext>["dealerGroups"],
  allLabel: string,
): string => {
  if (context.type === DEALER_CONTEXT_TYPE.ALL) return allLabel;
  if (context.type === DEALER_CONTEXT_TYPE.GROUP) {
    return dealerGroups.find((g) => g.id === context.id)?.name ?? "";
  }
  if (context.type === DEALER_CONTEXT_TYPE.DEALERSHIP) {
    for (const group of dealerGroups) {
      const dealer = group.dealers?.find(
        (d) => d.configurationId === context.id,
      );
      if (dealer) return dealer.name;
    }
  }
  return "";
};

const getCurrentDealership = (
  context: DealerContext,
  availableDealerships: DealershipWithGroup[],
): DealershipWithGroup | null => {
  if (context.type !== DEALER_CONTEXT_TYPE.DEALERSHIP) return null;
  return (
    availableDealerships.find((d) => d.configurationId === context.id) ?? null
  );
};

export const DealerContextSelector = () => {
  const { t } = useTranslation();
  const setDealerContextSelectorOpen = useScrollableOverlayStore(
    (s) => s.setDealerContextSelectorOpen,
  );
  const {
    dealerGroups,
    availableDealerships,
    context,
    setContext,
    isLoading,
    isGroupRole,
    isLytefloUser,
  } = useDealerContext();

  const isAllReservedForUser =
    context.type === DEALER_CONTEXT_TYPE.ALL && !isLytefloUser;

  const selectedKey =
    context.type === DEALER_CONTEXT_TYPE.ALL && !isAllReservedForUser
      ? DEALER_CONTEXT_TYPE.ALL
      : (context.id ?? undefined);

  const selectedLabel = isAllReservedForUser
    ? ""
    : findCurrentLabel(context, dealerGroups, t("contextSelector.all"));

  const handleSelect = (next: DealerContext) => {
    setContext(next);
  };

  return (
    <div className="lf-flex lf-w-full lf-max-w-72 lf-min-w-0 lf-items-center lf-justify-center lf-gap-2">
      {isGroupRole ? (
        <DealerContextDropdown
          inputClassName="lf-rounded-none"
          dealerGroups={dealerGroups}
          selectedKey={selectedKey}
          selectedLabel={selectedLabel}
          onSelect={handleSelect}
          onOpenChange={setDealerContextSelectorOpen}
          className="lf-w-full"
          isLoading={isLoading}
          showAllOption={isLytefloUser}
          showSectionCaptions={isLytefloUser}
        />
      ) : (
        <div className="lf-flex lf-w-full lf-items-center lf-justify-center">
          {getCurrentDealership(context, availableDealerships)?.name ?? ""}
        </div>
      )}
    </div>
  );
};
